<template>
    <div class="page-container">
        <!-- Desktop View -->
        <div class="desktop-view" v-if="!isMobile">
            <div class="page-header">
                <div class="header-content">
                    <h1 class="page-title">Transactions History</h1>
                    <p class="page-subtitle">History of your transaction & points</p>
                </div>
            </div>

            <div class="content-wrapper">
                <!-- Main Content -->
                <div class="main-content">
                    <!-- Point Summary Banner (Desktop) -->
                    <div v-if="isPointSystemActive && isFeatureFlagLoaded" class="point-banner desktop">
                        <div class="banner-main">
                            <div class="banner-left-desktop">
                                <div class="point-display">
                                    <div class="coin-icon">
                                        <img src="/img/member-icon/points-logo.svg" alt="" class="logo-img">
                                    </div>
                                    <div v-if="page.isLoading && page.isFirstLoad" class="skeleton-point-text"></div>
                                    <span v-else class="point-amount">{{ pointInfo.points !== null ? pointInfo.points.toLocaleString('id-ID') : '0' }} Poin</span>
                                    <!-- Recent deduction indicator (shows when points were recently expired) -->
                                    <button
                                        v-if="!page.isLoading && pointInfo.expiration && pointInfo.expiration.recent_deduction"
                                        class="recent-deduction-toggle"
                                        @click.stop="showRecentDeduction = !showRecentDeduction"
                                        title="Points recently expired"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                                            <path d="M12 7V13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                            <circle cx="12" cy="17" r="1.5" fill="currentColor"/>
                                        </svg>
                                    </button>
                                </div>
                                <div class="transaction-legend">
                                    <div class="legend-item">
                                        <span class="legend-dot dot-redeem"></span>
                                        <span class="legend-label">Earned</span>
                                    </div>
                                    <div class="legend-item">
                                        <span class="legend-dot dot-cancel"></span>
                                        <span class="legend-label">Deducted</span>
                                    </div>
                                    <div class="legend-item">
                                        <span class="legend-dot dot-pending"></span>
                                        <span class="legend-label">Pending</span>
                                    </div>
                                </div>
                            </div>
                            <button @click="$router.push('/point-info')" class="detail-link">Detail</button>
                        </div>

                        <!-- Recent Deduction Dropdown -->
                        <div v-if="showRecentDeduction && pointInfo.expiration && pointInfo.expiration.recent_deduction" class="recent-deduction-dropdown">
                            <div class="rdd-header">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                                    <path d="M12 7V13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                    <circle cx="12" cy="17" r="1.5" fill="currentColor"/>
                                </svg>
                                <span>Points Recently Expired</span>
                                <button class="rdd-close" @click.stop="showRecentDeduction = false">&times;</button>
                            </div>
                            <div class="rdd-body">
                                <div class="rdd-stat">
                                    <span class="rdd-label">Total Expired</span>
                                    <span class="rdd-value">-{{ pointInfo.expiration.recent_deduction.total_points.toLocaleString('id-ID') }} Poin</span>
                                </div>
                                <div class="rdd-stat">
                                    <span class="rdd-label">Transactions</span>
                                    <span class="rdd-value">{{ pointInfo.expiration.recent_deduction.count }}x deduction</span>
                                </div>
                                <div class="rdd-stat">
                                    <span class="rdd-label">Last Expired</span>
                                    <span class="rdd-value">{{ formatDate(pointInfo.expiration.recent_deduction.latest_at) }}</span>
                                </div>
                            </div>
                            <p class="rdd-footer">Check transaction list for details (marked as "Point Expiration Deduction")</p>
                        </div>

                        <!-- Upcoming Expiration Warning -->
                        <div v-if="!page.isLoading && pointInfo.expiration && pointInfo.expiration.amount > 0" class="expiration-info">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" class="info-icon">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                                <path d="M12 8V12L14 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                            <span>{{ pointInfo.expiration.amount.toLocaleString('id-ID') }} points will expire on {{ formatDate(pointInfo.expiration.date) }}</span>
                        </div>
                    </div>

                    <!-- Skeleton Loader -->
                    <SkeletonLoader v-if="page.isLoading && page.isFirstLoad" type="transaction" :count="5" />

                    <!-- Transaction List -->
                    <div v-if="page.output && !(page.isLoading && page.isFirstLoad)" class="transaction-list">
                        <my-transaction 
                            v-for="(item, index) in filteredHistory" 
                            :key="item.id || index"
                            :transaction="item"
                            :vouchers="vouchers"
                            @open-receipt="showModal(item)"
                        />
                        <no-items v-if="!filteredHistory.length" message="No transactions found" />
                    </div>

                    <!-- Pagination -->
                    <member-pagination 
                        v-if="!page.isLoading && filteredHistory.length" 
                        :page="meta.page" 
                        :total="meta.total" 
                        @callback="navPage"
                    />
                </div>

                <!-- Sidebar Filter & Sort -->
                <div class="sidebar">
                    <!-- Filter Section -->
                    <div class="filter-card">
                        <h3 class="card-title">Filter</h3>
                        
                        <div class="filter-group">
                            <label class="filter-label">Unit / Branch</label>
                            <div class="custom-select">
                                <select v-model="selectedBranch" @change="updateMerchants">
                                    <option value="">All Branch</option>
                                    <option v-for="branch in branches" :key="branch.id" :value="branch.name">
                                        {{ branch.name }}
                                    </option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Merchant / Outlet</label>
                            <div class="custom-select">
                                <select v-model="selectedMerchant">
                                    <option value="">All Merchant</option>
                                    <option v-for="merchant in filteredMerchants" :key="merchant.id" :value="merchant.name">
                                        {{ merchant.name }}
                                    </option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Date Range</label>
                            <div class="date-range-inputs">
                                <DatePicker
                                    v-model="startDate"
                                    :max="endDate"
                                    placeholder="Start Date"
                                    class="date-picker"
                                />
                                <span class="date-separator">to</span>
                                <DatePicker
                                    v-model="endDate"
                                    :min="startDate"
                                    placeholder="End Date"
                                    class="date-picker"
                                />
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Type</label>
                            <div class="custom-select">
                                <select v-model="selectedType">
                                    <option value="">All Type</option>
                                    <option value="normal">Normal</option>
                                    <option value="gift" v-if="isVoucherGiftActive">Gift</option>
                                    <option value="pending">Pending</option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="toggle-group" v-if="selectedType !== 'pending' && selectedType !== 'normal'">
                            <label class="toggle-container">
                                <input type="checkbox" v-model="hidePending" class="toggle-input">
                                <span class="toggle-slider"></span>
                            </label>
                            <span class="toggle-label">Hide Pending Transaction</span>
                        </div>

                        <div class="toggle-group" v-if="selectedType !== 'gift' && selectedType !== 'normal' && isVoucherGiftActive">
                            <label class="toggle-container">
                                <input type="checkbox" v-model="hideGift" class="toggle-input">
                                <span class="toggle-slider"></span>
                            </label>
                            <span class="toggle-label">Hide Gift Transaction</span>
                        </div>

                        <div class="button-group">
                            <button @click="applyFilter" class="btn-apply">Apply Filter</button>
                            <button @click="clearFilter" class="btn-clear">Clear Filter</button>
                        </div>
                    </div>

                    <!-- Sort Section -->
                    <div v-if="isPointSystemActive" class="filter-card">
                        <h3 class="card-title">Sort</h3>
                        
                        <div class="filter-group">
                            <label class="filter-label">Sort By</label>
                            <div class="custom-select">
                                <select v-model="sortBy">
                                    <option value="" disabled>Sort By</option>
                                    <option value="pointRequirement">Point</option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Sort Direction</label>
                            <div class="custom-select">
                                <select v-model="sortDirection">
                                    <option value="" disabled>Direction</option>
                                    <option value="asc">Lowest to Highest</option>
                                    <option value="desc">Highest to Lowest</option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="button-group">
                            <button @click="applySort" class="btn-apply">Apply Sort</button>
                            <button @click="clearSort" class="btn-clear">Clear Sort</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Mobile View -->
        <div class="mobile-view" v-else>
            <div class="top-background">
                <svg width="330" height="55" viewBox="0 0 330 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <ellipse cx="111" cy="-84" rx="219" ry="139" fill="currentColor"/>
                </svg>
            </div>
           
            <!-- Point Summary Banner (Mobile) -->
            <div v-if="isPointSystemActive && isFeatureFlagLoaded" class="point-banner-mobile">
                <div class="banner-card">
                    <div class="banner-content">
                        <div class="banner-left">
                            <div class="banner-points">
                                <div class="coin-icon-small">
                                    <img src="/img/member-icon/points-logo.svg" alt="" class="logo-img-small">
                                </div>
                                <!-- Skeleton for point amount -->
                                <div v-if="page.isLoading && page.isFirstLoad" class="skeleton-point-text mobile"></div>
                                <span v-else class="points-text">{{ pointInfo.points !== null ? pointInfo.points.toLocaleString('id-ID') : '0' }} Poin</span>
                                <!-- Recent deduction indicator (mobile) -->
                                <button
                                    v-if="!page.isLoading && pointInfo.expiration && pointInfo.expiration.recent_deduction"
                                    class="recent-deduction-toggle mobile"
                                    @click.stop="showRecentDeduction = !showRecentDeduction"
                                    title="Points recently expired"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                                        <path d="M12 7V13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                        <circle cx="12" cy="17" r="1.5" fill="currentColor"/>
                                    </svg>
                                </button>
                            </div>
                           <div class="transaction-legend mobile">
                                <div class="legend-item">
                                    <span class="legend-dot dot-redeem"></span>
                                    <span class="legend-label">Earned</span>
                                </div>
                                <div class="legend-item">
                                    <span class="legend-dot dot-cancel"></span>
                                    <span class="legend-label">Deducted</span>
                                </div>
                                <div class="legend-item">
                                    <span class="legend-dot dot-pending"></span>
                                    <span class="legend-label">Pending</span>
                                </div>
                            </div>
                        </div>
                        <div class="banner-right">
                            <button @click="$router.push('/point-info')" class="btn-detail-mobile">Detail</button>
                        </div>
                    </div>

                    <!-- Recent Deduction Notice (mobile) -->
                    <div v-if="showRecentDeduction && pointInfo.expiration && pointInfo.expiration.recent_deduction" class="recent-deduction-mobile">
                        <div class="rdm-content">
                            <span class="rdm-text">
                                <strong>-{{ pointInfo.expiration.recent_deduction.total_points.toLocaleString('id-ID') }}</strong> points expired on {{ formatDate(pointInfo.expiration.recent_deduction.latest_at) }}
                            </span>
                            <button class="rdm-close" @click.stop="showRecentDeduction = false">&times;</button>
                        </div>
                    </div>

                    <!-- Expiration info (only show when data is loaded and exists) -->
                    <div v-if="!page.isLoading && pointInfo.expiration && pointInfo.expiration.amount > 0" class="expiration-bar-mobile">
                        <span class="exp-text">{{ pointInfo.expiration.amount.toLocaleString('id-ID') }} points will expire on {{ formatDate(pointInfo.expiration.date) }}</span>
                    </div>
                </div>
            </div>

            <div class="mobile-header" :class="{ 'no-banner': !(isPointSystemActive && isFeatureFlagLoaded) }">
                <h2 class="mobile-title">All Transactions</h2>
                <div class="mobile-actions">
                    <button @click="openModal('filter')" class="filter-button">
                        <img src="/img/member-icon/filter-icon.svg" alt="filter-icon" width="14" height="14">
                    </button>
                    <button v-if="isPointSystemActive" @click="openModal('sort')" class="filter-button">
                        <img src="/img/member-icon/sort-icon.svg" alt="sort-icon" width="14" height="14">
                    </button>
                </div>
            </div>

            <!-- Skeleton Loading State (Mobile - only on first load) -->
            <SkeletonLoader v-if="page.isLoading && page.isFirstLoad" type="transaction" :count="4" />

            <!-- Transaction List -->
            <div v-if="page.output && !(page.isLoading && page.isFirstLoad)" class="mobile-transaction-list">
                <my-transaction 
                    v-for="(item, index) in filteredHistory" 
                    :key="item.id || index"
                    :transaction="item"
                    :vouchers="vouchers"
                    @open-receipt="showModal(item)"
                />
                <no-items v-if="!filteredHistory.length" message="No transactions found" />
            </div>

            <!-- Pagination -->
            <member-pagination 
                v-if="!page.isLoading && filteredHistory.length" 
                :page="meta.page" 
                :total="meta.total" 
                @callback="navPage"
            />

            <!-- Filter Modal -->
            <div v-if="isFilterModalOpen" class="modal-overlay" @click="closeModal">
                <div class="modal-content" @click.stop>
                    <div class="modal-header">
                        <h3>Filter</h3>
                        <button @click="closeModal" class="close-button">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </button>
                    </div>

                    <div class="modal-body">
                        <div class="filter-group">
                            <label class="filter-label">Unit / Branch</label>
                            <div class="custom-select">
                                <select v-model="selectedBranch" @change="updateMerchants">
                                    <option value="">All Branch</option>
                                    <option v-for="branch in branches" :key="branch.id" :value="branch.name">
                                        {{ branch.name }}
                                    </option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Merchant / Outlet</label>
                            <div class="custom-select">
                                <select v-model="selectedMerchant">
                                    <option value="">All Merchant</option>
                                    <option v-for="merchant in filteredMerchants" :key="merchant.id" :value="merchant.name">
                                        {{ merchant.name }}
                                    </option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Date Range</label>
                            <div class="date-range-inputs mobile">
                                <DatePicker
                                    v-model="startDate"
                                    :max="endDate"
                                    placeholder="Start Date"
                                    class="date-picker"
                                />
                                <span class="date-separator">to</span>
                                <DatePicker
                                    v-model="endDate"
                                    :min="startDate"
                                    placeholder="End Date"
                                    class="date-picker"
                                />
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Type</label>
                            <div class="custom-select">
                                <select v-model="selectedType">
                                    <option value="">All Type</option>
                                    <option value="normal">Normal</option>
                                    <option value="gift" v-if="isVoucherGiftActive">Gift</option>
                                    <option value="pending">Pending</option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="toggle-group" v-if="selectedType !== 'pending' && selectedType !== 'normal'">
                            <label class="toggle-container">
                                <input type="checkbox" v-model="hidePending" class="toggle-input">
                                <span class="toggle-slider"></span>
                            </label>
                            <span class="toggle-label">Hide Pending Transaction</span>
                        </div>

                        <div class="toggle-group" v-if="selectedType !== 'gift' && selectedType !== 'normal' && isVoucherGiftActive">
                            <label class="toggle-container">
                                <input type="checkbox" v-model="hideGift" class="toggle-input">
                                <span class="toggle-slider"></span>
                            </label>
                            <span class="toggle-label">Hide Gift Transaction</span>
                        </div>

                        <div class="button-group">
                            <button @click="applyFilter" class="btn-apply">Apply Filter</button>
                            <button @click="clearFilter" class="btn-clear">Clear Filter</button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Sort Modal -->
            <div v-if="isSortModalOpen && isPointSystemActive" class="modal-overlay" @click="closeModal">
                <div class="modal-content" @click.stop>
                    <div class="modal-header">
                        <h3>Sort</h3>
                        <button @click="closeModal" class="close-button">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </button>
                    </div>

                    <div class="modal-body">
                        <div class="filter-group">
                            <label class="filter-label">Sort By</label>
                            <div class="custom-select">
                                <select v-model="sortBy">
                                    <option value="" disabled>Sort By</option>
                                    <option value="pointRequirement">Point</option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Sort Direction</label>
                            <div class="custom-select">
                                <select v-model="sortDirection">
                                    <option value="" disabled>Direction</option>
                                    <option value="asc">Lowest to Highest</option>
                                    <option value="desc">Highest to Lowest</option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="button-group">
                            <button @click="applySort" class="btn-apply">Apply Sort</button>
                            <button @click="clearSort" class="btn-clear">Clear Sort</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>


        <!-- Transaction Receipt Modal -->
        <transaction-client
            v-if="isModalVisible"
            @parentCloseModal="closeModal"
            :editedItem="editedItem">
        </transaction-client>

        <!-- Gift Info Modal -->
        <div v-if="isGiftModalOpen" class="gift-modal-overlay" @click="closeModal">
            <div class="gift-modal-content" @click.stop>
                <div class="gift-modal-header">
                    <div class="gift-modal-icon">🎁</div>
                    <button @click="closeModal" class="gift-modal-close">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>
                </div>
                <div class="gift-modal-body">
                    <h2 class="gift-modal-title">{{ giftItem.detail_name }}</h2>
                    <p class="gift-modal-subtitle">
                        <span v-if="giftItem.branch_name || giftItem.merchant_name">
                            {{ giftItem.branch_name }} <span v-if="giftItem.branch_name && giftItem.merchant_name"> • </span> {{ giftItem.merchant_name }}
                        </span>
                        <span v-else>{{ giftItem.corporate_name }}</span>
                    </p>
                    <p class="gift-modal-date">Diterima: {{ formatGiftDate(giftItem.created_at) }}</p>

                    <div v-if="giftItem.gift_note" class="gift-modal-note">
                        <p class="gift-note-label">Pesan dari merchant:</p>
                        <p class="gift-note-text">{{ giftItem.gift_note }}</p>
                    </div>
                    <div v-else class="gift-modal-note gift-note-empty">
                        <p class="gift-note-text">Tidak ada pesan dari merchant.</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, onMounted, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { usePointSystem } from '@/composables/pointsystem'
import { useModalHistory } from '@/composables/useModalHistory'
import TransactionClient from './MemberTransaction.vue'
import MyTransaction from '../../components/member/MyTransaction.vue'
import DatePicker from '@/components/DatePicker.vue'
import SkeletonLoader from '@/components/member/SkeletonLoader.vue'
import NoItems from '@/components/member/NoItems.vue'
import MemberPagination from '@/components/member/MemberPagination.vue'

export default {
    components: {
        TransactionClient,
        MyTransaction,
        DatePicker,
        SkeletonLoader,
        NoItems,
        MemberPagination
    },
    setup() {
        const store = useStore()
        const route = useRoute()
        const { isPointSystemActive, isFeatureFlagLoaded } = usePointSystem()
        const isModalVisible = ref(false)
        const isGiftModalOpen = ref(false)
        const giftItem = ref({})
        const isFilterModalOpen = ref(false)
        const isSortModalOpen = ref(false)

        // Controls visibility of recent deduction dropdown in point badge
        const showRecentDeduction = ref(false)
        
        // Setup modal history management untuk transaction detail modal
        const { pushModalHistory: pushTransactionModal, popModalHistory: popTransactionModal } = useModalHistory({
            isVisible: isModalVisible,
            onClose: () => {
                isModalVisible.value = false
                // Clean up URL query params if coming from notification
                if (route.query.auto_open === 'true' || route.query.transaction_code) {
                    const newUrl = window.location.pathname
                    window.history.replaceState({}, document.title, newUrl)
                }
            },
            stateKey: 'transaction-detail'
        })
        
        // Setup modal history management untuk filter modal
        const { pushModalHistory: pushFilterModal, popModalHistory: popFilterModal } = useModalHistory({
            isVisible: isFilterModalOpen,
            onClose: () => {
                isFilterModalOpen.value = false
            },
            stateKey: 'filter-modal'
        })
        
        // Setup modal history management untuk sort modal
        const { pushModalHistory: pushSortModal, popModalHistory: popSortModal } = useModalHistory({
            isVisible: isSortModalOpen,
            onClose: () => {
                isSortModalOpen.value = false
            },
            stateKey: 'sort-modal'
        })

        const { pushModalHistory: pushGiftModal, popModalHistory: popGiftModal } = useModalHistory({
            isVisible: isGiftModalOpen,
            onClose: () => {
                isGiftModalOpen.value = false
                giftItem.value = {}
            },
            stateKey: 'dashboard-gift-modal'
        })
        const editedItem = ref({
            id: '',
            name: '',
            transaction_value: '',
            title: ''
        })

        const page = reactive({
            isLoading: true,
            output: false,
            clientDashboardData: null,
            isFirstLoad: true
        })

        const meta = computed(() => store.state.TransactionHistory.meta)
        const allData = ref([])
        const branches = ref([])
        const filteredMerchants = ref([])
        const selectedBranch = ref(null)
        const selectedMerchant = ref(null)
        const selectedType = ref('')
        const startDate = ref(null)
        const endDate = ref(null)
        const hidePending = ref(false)
        const hideGift = ref(false)
        const sortBy = ref('')
        const sortDirection = ref('')
        const filteredData = ref([])
        const vouchers = ref([])

        const filteredHistory = computed(() => filteredData.value)
        const isMobile = ref(window.innerWidth < 992)
        const isVoucherGiftActive = computed(() => store.getters['FeatureFlag/getVoucherGiftFlag'] !== 'NONACTIVE')

        const pointInfo = ref({
            points: null,
            expiration: {
                amount: 0,
                date: null,
                days_remaining: null
            }
        })

        window.addEventListener('resize', () => {
            isMobile.value = window.innerWidth < 992
        })

        const openModal = (type) => {
            if (type === 'filter') {
                isFilterModalOpen.value = true
                isSortModalOpen.value = false
                pushFilterModal()
            } else if (type === 'sort') {
                isSortModalOpen.value = true
                isFilterModalOpen.value = false
                pushSortModal()
            }
        }

        const clearFilter = () => {
            selectedBranch.value = null
            selectedMerchant.value = null
            selectedType.value = ''
            startDate.value = null
            endDate.value = null
            hidePending.value = false
            hideGift.value = false
            loadState()
            closeModal()
        }

        const applySort = () => {
            applyFilter()
            closeModal()
        }

        const clearSort = () => {
            sortBy.value = ''
            sortDirection.value = ''
            applySort()
            closeModal()
        }

        const applyFilter = () => {
            const _meta = { ...meta.value, page: 1 }
            store.commit('TransactionHistory/switch_page', { meta: _meta })
            loadState()
        }

        onMounted(() => {
            const _meta = { ...meta.value, page: 1 }
            store.commit('TransactionHistory/switch_page', { meta: _meta })

            const urlParams = new URLSearchParams(window.location.search)
            if (urlParams.has('token')) {
                urlParams.delete('token')
                window.history.replaceState({}, document.title, window.location.pathname)
            }
            loadState()
            
            // Auto-open modal if coming from push notification
            if (route.query.auto_open === 'true' && route.query.transaction_code) {
                // Auto-opening modal for transaction
                
                // Wait for data to load, then find and open the transaction
                const checkDataInterval = setInterval(() => {
                    if (!page.isLoading && page.output) {
                        clearInterval(checkDataInterval)
                        
                        // Find transaction by code
                        const transaction = filteredHistory.value.find(
                            t => t.transaction_code === route.query.transaction_code || t.code === route.query.transaction_code
                        )
                        
                        if (transaction) {
                            // Transaction found, opening modal
                            showModal(transaction)
                            
                            // Clean up URL query params
                            const newUrl = window.location.pathname
                            window.history.replaceState({}, document.title, newUrl)
                        } else {
                            // Transaction not found
                        }
                    }
                }, 500) // Check every 500ms
                
                // Timeout after 10 seconds
                setTimeout(() => {
                    clearInterval(checkDataInterval)
                }, 10000)
            }
        })

        function showModal(data) {
            // Gift vouchers open the gift info modal instead
            if (data.type === 'gift') {
                giftItem.value = data
                isGiftModalOpen.value = true
                pushGiftModal()
                return
            }

            editedItem.value = {
                id: data.id,
                member_name: page.clientDashboardData.member_name,
                member_email: page.clientDashboardData.member_email,
                phone: page.clientDashboardData.member_profile.phone,
                state: page.clientDashboardData.member_profile.state,
                address: page.clientDashboardData.member_profile.address,
                country: page.clientDashboardData.member_profile.country,
                merchant_id: data.merchant_id,
                corporate_name: data.corporate_name,
                branch_name: data.branch_name,
                merchant_name: data.merchant_name,
                type: data.type,
                created_at: data.created_at,
                qty: data.qty,
                detail_name: data.detail_name || 'Deduction Point',
                detail_code: data.detail_code,
                transaction_code: data.transaction_code,
                transaction_value: data.subtotal,
                voucher_discount: data.voucher_discount,
                amount_after_voucher: data.amount_after_voucher,
                voucher_detail: data.voucher_detail,
                point: (data.point !== undefined && data.point !== null)
                    ? data.point
                    : (data.point_deducted !== undefined && data.point_deducted !== null)
                        ? `-${Math.abs(data.point_deducted)}`
                        : null,
                business_logo: data.merchant?.business_profile?.logo?.image_url ||
                    data.merchant?.businessProfile?.logo?.image_url ||
                    data.branch?.business_profile?.logo?.image_url ||
                    data.branch?.businessProfile?.logo?.image_url ||
                    page.clientDashboardData.branch_images?.[0]?.business_profile?.logo?.image_url ||
                    page.clientDashboardData.branch_images?.[0]?.businessProfile?.logo?.image_url,
                fallback_logo: data.branch?.business_profile?.logo?.image_url ||
                    data.branch?.businessProfile?.logo?.image_url ||
                    page.clientDashboardData.branch_images?.[0]?.business_profile?.logo?.image_url ||
                    page.clientDashboardData.branch_images?.[0]?.businessProfile?.logo?.image_url,
                reference_code: data.refference_code,
                is_pending: data.is_pending,
                tax_charge: data.tax_charge,
                service_charge: data.service_charge,
                tax_value: data.tax_value,
                service_value: data.service_value,
                before_tax_service: data.before_tax_service,
                after_tax_service: data.after_tax_service,
                is_nett_price: data.is_nett_price,
                title: 'My Transaction Receipt'
            }
            isModalVisible.value = true
            pushTransactionModal()
        }

        function closeModal() {
            // Close all modals
            if (isModalVisible.value) {
                isModalVisible.value = false
                popTransactionModal()
            }
            
            if (isGiftModalOpen.value) {
                isGiftModalOpen.value = false
                popGiftModal()
            }
            
            if (isFilterModalOpen.value) {
                isFilterModalOpen.value = false
                popFilterModal()
            }
            
            if (isSortModalOpen.value) {
                isSortModalOpen.value = false
                popSortModal()
            }
        }

        function formatGiftDate(dateStr) {
            if (!dateStr) return ''
            const date = new Date(dateStr)
            if (isNaN(date.getTime())) return dateStr
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
            const day = String(date.getDate()).padStart(2, '0')
            const month = months[date.getMonth()]
            const year = date.getFullYear()
            let hours = date.getHours()
            const minutes = String(date.getMinutes()).padStart(2, '0')
            const period = hours >= 12 ? 'PM' : 'AM'
            hours = hours % 12 || 12
            return `${day} ${month} ${year} • ${String(hours).padStart(2, '0')}.${minutes} ${period}`
        }

        function formatDate(dateStr) {
            if (!dateStr) return ''
            const date = new Date(dateStr)
            if (isNaN(date.getTime())) return dateStr
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
            return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
        }

        const loadState = () => {
            page.isLoading = true
            const filter = {}

            if (selectedBranch.value) {
                filter.branch_id = selectedBranch.value
            }

            if (selectedMerchant.value) {
                filter.merchant_id = selectedMerchant.value
            }

            if (startDate.value && endDate.value) {
                const formattedStart = startDate.value
                const formattedEnd = endDate.value
                filter.dateRange = `${formattedStart}, ${formattedEnd}`
            }

            if (selectedType.value) {
                filter.type = selectedType.value
            }

            if (hidePending.value) {
                filter.hide_pending = true
            }

            if (hideGift.value) {
                filter.hide_gift = true
            }

            if (sortBy.value && sortDirection.value) {
                filter.sortBy = sortBy.value
                filter.sortDirection = sortDirection.value
            }

            store.commit('TransactionHistory/add_filter', { filter })
            store.commit('ClientDashboard/add_filter', { filter })

            store.dispatch('ClientDashboard/read').then((data) => {
                page.clientDashboardData = data.data.data
            }).catch(() => {
                // Error loading dashboard data
            })

            // Fetch deals/vouchers dictionary for transaction icons (Name resolution)
            store.dispatch('ClientDeals/read')
                .then(response => {
                    if (response.data && response.data.data && response.data.data.voucher) {
                        vouchers.value = response.data.data.voucher
                    }
                })
                .catch(() => {
                    // Error loading deals dictionary
                })

            store.dispatch('TransactionHistory/read')
                .then((res) => {
                    const filterData = res.data.filter.filter
                    branches.value = filterData

                    const fetchedData = Array.isArray(res.data.filter.all_data) ? res.data.filter.all_data : []
                    allData.value = fetchedData

                    if (res.data.points !== undefined) {
                      pointInfo.value.points = res.data.points
                      pointInfo.value.expiration = res.data.point_expiration
                    }

                    if (fetchedData.length > 0) {
                        filteredData.value = allData.value
                    } else {
                        filteredData.value = []
                    }

                    if (branches.value.length > 0 && !selectedBranch.value) {
                        selectedBranch.value = ''
                    }
                    updateMerchants()
                    if (isMobile.value) {
                        closeModal()
                    }
                })
                .catch(() => {
                    // Error loading transaction history
                })
                .finally(() => {
                    page.isLoading = false
                    page.output = true
                    page.isFirstLoad = false
                })
        }

        const updateMerchants = () => {
            if (selectedBranch.value === '') {
                let allMerchants = []
                branches.value.forEach(branch => {
                    allMerchants = allMerchants.concat(branch.merchants)
                })
                filteredMerchants.value = allMerchants
            } else {
                const selected = branches.value.find(branch => branch.name === selectedBranch.value)
                filteredMerchants.value = selected ? selected.merchants : []
            }

            if (!selectedMerchant.value) {
                selectedMerchant.value = ''
            }
        }

        const navPage = (page) => {
            const _meta = { ...meta.value, page }
            store.commit('TransactionHistory/switch_page', { meta: _meta })
            loadState()
        }

        watch(
            () => selectedBranch.value,
            (newVal) => {
                if (newVal === '') {
                    selectedMerchant.value = ''
                }
            }
        )

        watch(
            () => selectedType.value,
            () => {
                hidePending.value = false
                hideGift.value = false
            }
        )

        watch([isModalVisible, isFilterModalOpen, isSortModalOpen, isGiftModalOpen], (vals) => {
            const anyOpen = vals.some(Boolean)
            document.body.style.overflow = anyOpen ? 'hidden' : ''
        })

        onBeforeUnmount(() => {
            document.body.style.overflow = ''
        })

        return {
            page,
            meta,
            loadState,
            navPage,
            isModalVisible,
            editedItem,
            showModal,
            closeModal,
            isGiftModalOpen,
            giftItem,
            formatGiftDate,
            filteredHistory,
            branches,
            filteredMerchants,
            selectedBranch,
            selectedMerchant,
            selectedType,
            updateMerchants,
            startDate,
            endDate,
            hidePending,
            hideGift,
            applyFilter,
            clearFilter,
            applySort,
            clearSort,
            sortBy,
            sortDirection,
            isMobile,
            isFilterModalOpen,
            isSortModalOpen,
            openModal,
            vouchers,
            isPointSystemActive,
            isFeatureFlagLoaded,
            pointInfo,
            formatDate,
            showRecentDeduction,
            isVoucherGiftActive
        }
    }
}
</script>

<style scoped>
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

.filter-button {
    background-color: var(--primary-color, #F93E3E);
    border: none;

    padding: 6px 10px;
    border-radius: 8px;
    transition: background-color 0.3s;
}

.filter-button img {
    width: 14px;
    height: 14px;
}


.top-background {
    position: absolute;
    top: -8px;
    left: -32px;
    color: var(--primary-color, #F93E3E);
}

.header-text-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    color: white;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
}

.welcome-text {
    font-size: 10px;
    font-weight: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
}

.member-name-title {
    font-size: 1.2rem;
    font-weight: 520;
    letter-spacing: -2%;
    word-break: break-word;
    overflow-wrap: anywhere;
    line-height: 1.3;
}

.page-container {
    width: 100%;
    min-height: 100vh;
}

/* Desktop View */
.desktop-view {
    margin: 0 auto;
    padding: 24px;
}

.page-header {
    margin-bottom: 32px;
}

.page-title {
    font-size: 28px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 8px;
}

.page-subtitle {
    font-size: 14px;
    color: #6B7280;
}

.content-wrapper {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 24px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
}

.main-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
}

.transaction-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
}

.sidebar {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
}

.filter-card {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.card-title {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 20px;
}

.filter-group {
    margin-bottom: 20px;
}

.filter-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
}

.custom-select {
    position: relative;
}

.custom-select select {
    width: 100%;
    padding: 12px 40px 12px 16px;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    font-size: 14px;
    color: #111827;
    background: white;
    cursor: pointer;
    appearance: none;
    outline: none;
    transition: all 0.2s;
}

.custom-select select:hover {
    border-color: #D1D5DB;
}

.custom-select select:focus {
    border-color: var(--primary-border, #F93E3E);
    box-shadow: 0 0 0 3px var(--secondary-color, rgba(249, 62, 62, 0.1));
}

.select-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #6B7280;
}

.date-range-inputs {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 6px;
    min-width: 0;
    width: 100%;
}

.date-input {
    min-width: 0; /* Prevent overflow */
    padding: 12px 16px;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    font-size: 14px;
    color: #111827;
    outline: none;
    transition: all 0.2s;
    width: 100%;
    box-sizing: border-box;
}

.date-input:hover {
    border-color: #D1D5DB;
}

.date-input:focus {
    border-color: var(--primary-border, #F93E3E);
    box-shadow: 0 0 0 3px var(--secondary-color, rgba(249, 62, 62, 0.1));
}

/* DatePicker Styling */
.date-picker {
    width: 100%;
    min-width: 0;
}

.date-picker :deep(.date-input-trigger) {
    width: 100%;
    min-height: 44px;
    padding: 8px 10px;
    gap: 4px;
    box-sizing: border-box;
}

.date-picker :deep(.selected-date) {
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.date-range-inputs.mobile .date-picker {
    margin-bottom: 8px;
}

.date-range-inputs.mobile .date-picker:last-child {
    margin-bottom: 0;
}

.date-separator {
    font-size: 14px;
    color: #6B7280;
}

.toggle-group {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
}

.toggle-container {
    position: relative;
    width: 44px;
    height: 24px;
    cursor: pointer;
}

.toggle-input {
    opacity: 0;
    width: 0;
    height: 0;
}

.toggle-slider {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #E5E7EB;
    border-radius: 24px;
    transition: 0.3s;
}

.toggle-slider:before {
    content: "";
    position: absolute;
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    border-radius: 50%;
    transition: 0.3s;
}

.toggle-input:checked + .toggle-slider {
    background-color: var(--primary-color, #F93E3E);
}

.toggle-input:checked + .toggle-slider:before {
    transform: translateX(20px);
}

.toggle-label {
    font-size: 14px;
    color: #374151;
}

.button-group {
    display: flex;
    gap: 12px;
}

.btn-apply,
.btn-clear {
    flex: 1;
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-apply {
    background-color: var(--primary-color, #F93E3E);
    color: white;
}

.btn-apply:hover {
    opacity: 0.9;
}

.btn-clear {
    background-color: white;
    color: #374151;
    border: 1px solid #E5E7EB;
}

.btn-clear:hover {
    background-color: #F9FAFB;
}

/* Loading removed - using skeleton instead */

.empty-state {
    text-align: center;
    padding: 60px 20px;
    background: white;
    border-radius: 12px;
}

.empty-state p {
    font-size: 14px;
    color: #6B7280;
}

.pagination-container {
    margin-top: 24px;
}


.mobile-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
}

.mobile-header.no-banner {
    margin-top: 64px;
}

.mobile-title {
    font-size: 18px;
    font-weight: 520;
    margin: 0 !important;
}

.mobile-actions {
    display: flex;
    gap: 8px;
}

.icon-button {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--primary-color, #F93E3E);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
}


.icon-button svg {
    color: white;
}

.mobile-transaction-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

.modal-content {
    background: white;
    border-radius: 20px 20px 0 0;
    width: 100%;
    max-width: 600px;
    max-height: 80vh;
    overflow-y: auto;
    animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #E5E7EB;
}

.modal-header h3 {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
}

.close-button {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    color: #6B7280;
}

.modal-body {
    padding: 20px;
}

.date-range-inputs.mobile {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.date-range-inputs.mobile .date-separator {
    align-self: flex-start;
    margin: 4px 0;
}

/* Responsive */
@media (max-width: 1280px) {
    .desktop-view {
        padding: 16px;
    }

    .content-wrapper {
        grid-template-columns: 1fr 280px;
        gap: 16px;
    }

    .filter-card {
        padding: 20px 16px;
    }
}

/* Mobile & Tablet (< 992px) */
@media (max-width: 991px) {
    .content-wrapper {
        grid-template-columns: 1fr;
    }

    .sidebar {
        order: -1;
    }

    .desktop-view {
        display: none;
    }

    .mobile-view {
        display: flex;
        flex-direction: column;
        min-height: 100dvh;
        padding-bottom: 96px;
    }
}

.gift-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    backdrop-filter: blur(2px);
    animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
}

.gift-modal-content {
    background: #fff;
    border-radius: 20px;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
    overflow: hidden;
    animation: slideUp 0.25s ease;
}

@keyframes slideUp {
    from { transform: translateY(32px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
}

.gift-modal-header {
    background: linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 20px 16px;
}

.gift-modal-icon {
    font-size: 40px;
    line-height: 1;
}

.gift-modal-close {
    background: rgba(255, 255, 255, 0.7);
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #065F46;
    transition: background 0.2s;
}

.gift-modal-close:hover {
    background: rgba(255, 255, 255, 0.95);
}

.gift-modal-body {
    padding: 20px 24px 28px;
}

.gift-modal-title {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 6px;
    line-height: 1.3;
}

.gift-modal-subtitle {
    font-size: 13px;
    color: #6B7280;
    margin: 0 0 4px;
}

.gift-modal-date {
    font-size: 12px;
    color: #9CA3AF;
    margin: 0 0 20px;
}

.gift-modal-note {
    background: #F0FDF4;
    border: 1px solid #BBF7D0;
    border-radius: 12px;
    padding: 14px 16px;
}

.gift-modal-note.gift-note-empty {
    background: #F9FAFB;
    border-color: #E5E7EB;
}

.gift-note-label {
    font-size: 11px;
    font-weight: 600;
    color: #065F46;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 6px;
}

.gift-note-text {
    font-size: 14px;
    color: #374151;
    line-height: 1.6;
    margin: 0;
    white-space: pre-wrap;
}

.gift-note-empty .gift-note-text {
    color: #9CA3AF;
    font-style: italic;
}

/* Point Summary Banner Styles */
.point-banner {
    margin-bottom: 24px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.point-banner.desktop {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.banner-main {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.banner-left-desktop {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.point-display {
    display: flex;
    align-items: center;
    gap: 16px;
}

.coin-icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.logo-img {
    width: 36px;
    height: 36px;
    object-fit: contain;
}

.point-amount {
    font-size: 28px;
    font-weight: 800;
    color: var(--primary-color, #b91c1c);
    letter-spacing: -0.02em;
}

.detail-link {
    background: white;
    border: 1px solid var(--primary-color, #f93e3e);
    color: var(--primary-color, #f93e3e);
    padding: 8px 24px;
    border-radius: 100px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}


.expiration-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px dashed #e5e7eb;
    color: var(--primary-color, #f93e3e);
    font-size: 13px;
    font-weight: 500;
}

.info-icon {
    flex-shrink: 0;
}

/* Recent Deduction Toggle Button */
.recent-deduction-toggle {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--primary-color, #f93e3e);
    padding: 2px;
    margin-left: 4px;
    display: flex;
    align-items: center;
    border-radius: 50%;
    transition: all 0.2s;
    animation: pulse-primary 2s ease-in-out infinite;
}

.recent-deduction-toggle:hover {
    color: var(--primary-color, #f93e3e);
    background: #f3f4f6;
}

.recent-deduction-toggle.mobile {
    margin-left: 2px;
}

@keyframes pulse-primary {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
}

/* Recent Deduction Dropdown (Desktop) */
.recent-deduction-dropdown {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px 14px;
    margin-top: 10px;
    animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
    from { opacity: 0; transform: translateY(-6px); }
    to { opacity: 1; transform: translateY(0); }
}

.rdd-header {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--primary-color, #f93e3e);
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.rdd-close {
    margin-left: auto;
    background: none;
    border: none;
    color: var(--primary-color, #f93e3e);
    font-size: 18px;
    cursor: pointer;
    line-height: 1;
    padding: 0 2px;
    opacity: 0.6;
    transition: opacity 0.2s;
}

.rdd-close:hover {
    opacity: 1;
}

.rdd-body {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
}

.rdd-stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.rdd-label {
    font-size: 10px;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.rdd-value {
    font-size: 13px;
    font-weight: 600;
    color: var(--primary-color, #f93e3e);
}

.rdd-footer {
    font-size: 11px;
    color: #6b7280;
    opacity: 0.8;
    margin-top: 8px;
    margin-bottom: 0;
    font-style: italic;
}

/* Recent Deduction Notice (Mobile) */
.recent-deduction-mobile {
    padding: 8px 12px;
    background: #f9fafb;
    border-top: 1px solid #e5e7eb;
}

.rdm-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.rdm-text {
    font-size: 11.5px;
    color: #4b5563;
}

.rdm-text strong {
    color: var(--primary-color, #f93e3e);
}

.rdm-close {
    background: none;
    border: none;
    color: #9ca3af;
    font-size: 16px;
    cursor: pointer;
    line-height: 1;
    padding: 0;
    flex-shrink: 0;
    transition: color 0.2s;
}

.rdm-close:hover {
    color: #4b5563;
}

/* Mobile Banner */
.point-banner-mobile {
    margin-bottom: 24px;
    margin-top: 64px;
}

.banner-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
    overflow: hidden;
}

.banner-content {
    padding: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.banner-left {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.banner-points {
    display: flex;
    align-items: center;
    gap: 8px;
}

.coin-icon-small {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.logo-img-small {
    width: 24px;
    height: 24px;
    object-fit: contain;
}

.points-text {
    font-size: 20px;
    font-weight: 800;
    color: var(--primary-color, #b91c1c);
}

.transaction-legend {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 6px;
}

.transaction-legend.mobile {
    padding-left: 6px;
    gap: 12px;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #4b5563;
    font-weight: 500;
}

.legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
}

.legend-dot.dot-redeem {
    background-color: #00AA00;
}

.legend-dot.dot-cancel {
    background-color: #D92D20;
}

.legend-dot.dot-pending {
    background-color: #6B7280;
}

.btn-detail-mobile {
    background: white;
    border: 1px solid var(--primary-color, #f93e3e);
    color: var(--primary-color, #f93e3e);
    padding: 6px 16px;
    border-radius: 100px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}

.expiration-bar-mobile {
    background: white;
    padding: 10px 20px;
    border-top: 1px solid #e5e7eb;
}

.skeleton-point-text,
.skeleton-expiration-text {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 8px;
}

.skeleton-point-text {
    width: 150px;
    height: 28px;
}

.skeleton-point-text.mobile {
    width: 120px;
    height: 20px;
}

.skeleton-expiration-text {
    width: 100%;
    height: 36px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed #e5e7eb;
}

.skeleton-expiration-text.mobile {
    height: 32px;
    margin-top: 0;
    padding-top: 0;
    border-top: none;
    background: white;
    padding: 10px 20px;
    border-top: 1px solid #e5e7eb;
}

@keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}

.exp-text {
    font-size: 11px;
    color: var(--primary-color, #DC2626);
    font-weight: 500;
}

</style>